import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Loader from "./components/UI/Loader";
import { useAuth0 } from "@auth0/auth0-react";
import Dashboard from "./components/dashboard/Dashboard";
import { ProtectedLayout } from "./components/providers/AuthGuard";
import AdminNavigation from "components/admin/navigation";
import { ADMIN_ROLE, rolePrefix } from "constants/common";
import { apiInstance } from "services/api.service";
import { Toaster } from "react-hot-toast";
import usePageAnalytics from "hooks/usePageAnalytics";
import { SocketProvider } from "context/SocketContext";
import GuestQuestionnaire from "pages/guest-questionnaire";
import CookieConsentBanner from "components/CookieConsentBanner";

function App() {
  // Initialize Google Analytics
  usePageAnalytics();
  // Initialize Auth
  const {
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    user,
    getAccessTokenSilently,
  } = useAuth0();
  const [redirectLoading, setRedirectLoading] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function redirectAuth() {
      try {
        setRedirectLoading(true);
        await loginWithRedirect({
          appState: {
            returnTo: "/dashboard/home",
          },
        });
      } catch (error) {
      } finally {
        setRedirectLoading(false);
      }
    }
    if (location.pathname.includes("guest-questionnaire")) {
      return;
    } else if (!isAuthenticated && !isLoading) {
      redirectAuth();
    } else if (isAuthenticated && !isLoading && user) {
      const roles = user?.[rolePrefix];
      if (roles.includes(ADMIN_ROLE)) {
        getAccessTokenSilently().then((token) => {
          apiInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
          localStorage.setItem("token", token);
          navigate("/admin", { replace: true });
        });
        return;
      } else navigate("/dashboard/home", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, user]);

  if (isLoading || redirectLoading) {
    return <Loader />;
  }

  // Unauthenticated visitors are about to be bounced to Auth0, so rendering the
  // banner here would only flash it for a frame. It is shown once they land
  // back in the app, and on the public questionnaire route which never redirects.
  const canShowCookieBanner =
    isAuthenticated || location.pathname.includes("guest-questionnaire");

  return (
    <SocketProvider>
      {canShowCookieBanner && <CookieConsentBanner />}
      <Helmet>
        <title>Dashboard | Valoo</title>
      </Helmet>
      <Toaster position="top-center" />
      <Routes>
        <Route path="/" Component={Loader} />
        <Route
          path="/guest-questionnaire/:userType"
          element={<GuestQuestionnaire />}
        />
        <Route path="/dashboard" element={<ProtectedLayout />}>
          <Route path="home" element={<Dashboard />} />
        </Route>
        <Route path="/callback" Component={Loader} />
        <Route path="/admin/*" element={<AdminNavigation />} />
      </Routes>
    </SocketProvider>
  );
}

export default App;
